/**
 * Contract constants. These are engine identity, NOT config.
 *
 * Moving any of these into designer-editable config would silently
 * rebalance every timing in the game and invalidate every recorded replay
 * at once, which is why they live in source and are covered by
 * ENGINE_VERSION.
 */

export const TICK_RATE = 60;
export const ENGINE_VERSION = 1;

/** Bumped when PRNG, input encoding, stream set, or tick order changes. */
export const REPLAY_FORMAT_VERSION = 1;

/** 8 minutes. Reaching this is outcome `timeout`, and is a balance bug. */
export const RUN_MAX_TICKS = TICK_RATE * 60 * 8;

/**
 * There is deliberately no AwaitingChoice phase. An earlier design paused
 * the run to present upgrade cards; stopping a corridor runner every few
 * seconds to read a menu destroys the only thing the genre sells, so
 * boons now resolve instantly and the phase that served the modal is gone
 * rather than left behind as unreachable code.
 */
export const Phase = { Running: 0, Ended: 2 } as const;
export type PhaseValue = (typeof Phase)[keyof typeof Phase];

export const Outcome = {
  None: 0, Died: 1, BossKilled: 2, Timeout: 3, Quit: 4,
} as const;
export type OutcomeValue = (typeof Outcome)[keyof typeof Outcome];

export const OUTCOME_NAMES = ['none', 'died', 'bossKilled', 'timeout', 'quit'] as const;

/**
 * RNG streams. Order is part of the replay contract: a stream's index
 * seeds it, so reordering this list reseeds everything.
 * `Cosmetic` is never read by sim code — render-side jitter only.
 */
export const Stream = {
  Waves: 0, Drops: 1, UpgradeOffers: 2, Combat: 3, Cosmetic: 4,
} as const;
export const STREAM_NAMES = ['waves', 'drops', 'upgradeOffers', 'combat', 'cosmetic'] as const;
export const STREAM_COUNT = STREAM_NAMES.length;

/**
 * THE EVENT BUS RECORD SHAPE — see docs/EVENT_VOCABULARY.md (binding).
 *
 *   [ kind, subject, p0, p1, p2, p3, p4, p5 ]
 *
 * `subject` is the pool index the event happens TO, or -1 where there is no
 * entity subject. Unused payload slots are written as 0.
 *
 * Fixed stride, not variable-length records: reading is branch-free index
 * arithmetic and a buffer malformed by miscounting is impossible to produce.
 * Bit-packing was rejected — it saves nothing on a per-tick buffer this small
 * and risks sign/precision bugs against the hash's `(v * 4096) | 0`.
 *
 * The bus has THREE consumers, not one: animation (M-rules), audio (F1/F2)
 * and time (F3/F4). All three key off the SAME event and none derives its own
 * trigger (F5), which is why `tier` and `lethal` ride on the hit itself.
 */
export const EVENT_HEADER_SLOTS = 2;
export const EVENT_PAYLOAD_SLOTS = 6;
export const EVENT_STRIDE = 8;

// --- Compile-time assertion: stride === header + payload -------------------
// Adding a payload slot without widening the stride must fail the BUILD, not
// silently truncate a payload at runtime where it would surface as a renderer
// bug hours later.
type BuildTuple<N extends number, T extends unknown[] = []> =
  T['length'] extends N ? T : BuildTuple<N, [...T, unknown]>;
type Add<A extends number, B extends number> =
  [...BuildTuple<A>, ...BuildTuple<B>]['length'];
type AssertTrue<T extends true> = T;
export type _EventStrideIsConsistent = AssertTrue<
  Add<typeof EVENT_HEADER_SLOTS, typeof EVENT_PAYLOAD_SLOTS> extends typeof EVENT_STRIDE
    ? true : false>;

/**
 * Event kinds written to the per-tick buffer for the renderer.
 *
 * Numbering is APPEND-ONLY. Kinds 0-16 keep their values so stored logs and
 * existing renderer code stay readable; 1 (`Killed`) and 2 (`Damaged`) are
 * retired holes, superseded by `Death` and `Hit` which carry the direction,
 * tier and lethality that M5/F1/F3 need and the originals could not express.
 */
export const Ev = {
  /** A body entered the world. subject = idx, p0 = defIdx, p1 = x, p2 = y. */
  Spawned: 0,
  // 1, 2 — retired (Killed, Damaged). See Death (22) and Hit (17).
  PickedUp: 3,
  GatePassed: 4, LevelUp: 5, BossPhase: 6, SquadHit: 7,
  Armory: 8,
  /** Barrier payout resolved. p0 = value, p1 = squad delta. Economy, not the panel. */
  BarrierDown: 9,
  Boon: 10, Breach: 11,
  /** A charge station resolved. p0 = ChargeTier, p1 = the op that paid out. */
  Charged: 12,
  /** An ally was deployed (p0 = pool index) or died (p1 = 1). */
  Ally: 13,
  /** The shield absorbed a hit (p0 = damage soaked, p1 = shield left). */
  ShieldHit: 14,
  /** A monolith appeared (subject = pool index, p0 = titan kind). */
  Monolith: 15,
  /** A monolith broke open (subject = pool index, p0 = titan kind). */
  TitanFreed: 16,

  // --- C3 vocabulary. dx/dy are components of a UNIT vector, normalised with
  // sqrt. Never an angle: atan2/sin/cos have no cross-engine guarantee and are
  // the classic replay-desync source, while sqrt is IEEE correctly-rounded.
  /** subject = victim. p0 = dx, p1 = dy, p2 = tier, p3 = lethal (0/1). */
  Hit: 17,
  /** subject = victim. p0 = dx, p1 = dy, p2 = severity. */
  Stagger: 18,
  /** subject = pushed. p0 = dx, p1 = dy, p2 = mag (world units). */
  Push: 19,
  /** subject = moved. p0 = source idx, p1 = hop depth. */
  DisplacedBy: 20,
  /** subject = victim. p0 = dx, p1 = dy. */
  BurnStart: 21,
  /** subject = victim. p0 = cause, p1 = dx, p2 = dy. */
  Death: 22,
  /** subject = blocker. p0 = dx, p1 = dy, p2 = tier. */
  Block: 23,
  /** The physical gate panel broke under fire. subject = gate idx, p0 = op, p1 = value. */
  GateBreak: 24,
  /** A-43: the player dashed. subject = -1. Render/audio cue only — the
   *  invulnerability window itself is `RunState.dodgeActiveTicks`. */
  Dodge: 25,
} as const;

/** Why a body died — `Death.p0`. The fall arc is a consequence of the blow. */
export const DeathCause = {
  /** Ran out of health from accumulated fire. */
  Attrition: 0,
  /** A single lethal blow. */
  Blow: 1,
  /** Burned down after the R10 material swap. */
  Burn: 2,
  /** Reached the line and was spent against the squad. */
  Breach: 3,
} as const;

/**
 * What a charge station pays, decided by how full it was when it arrived.
 *
 * The station is the game's moat: the reward is not "steer into a lane",
 * it is "how much of your firepower did you spend on this instead of on
 * the horde flooding the other lane". A binary broke/not-broke made that
 * an all-or-nothing coin flip that players could not read mid-approach.
 * Three tiers give the investment a gradient -- every bullet moves the
 * needle, and the player can see which side of a threshold they are on.
 */
export const ChargeTier = { Fizzle: 0, Partial: 1, Full: 2 } as const;

/**
 * TITANS: what is entombed inside a monolith.
 *
 * Variety is the point. A single "ally" makes every monolith the same
 * decision, so the player learns it once and stops looking. Each titan
 * fights differently enough that WHICH rock is beside you changes whether
 * the rock is worth your bullets at all -- an artillery platform when the
 * lane is already clear is a waste, and a bulwark when you are drowning
 * is the run.
 */
export const Titan = {
  /** Walks the lane and smashes. All-round, the baseline. */
  Saurian: 0,
  /** Heavy ranged: fewer, far bigger hits, reaches deep up the corridor. */
  Mech: 1,
  /** Enormous hp, low damage: it BLOCKS a lane and refuses to die. */
  Bulwark: 2,
  /** Sweeps a wide arc for less per hit -- built for packed hordes. */
  Artillery: 3,
} as const;
export const TITAN_COUNT = 4;
/** Below this the station sparks out and pays NOTHING. */
export const CHARGE_PARTIAL = 0.4;
/** At or above this the prize arrives at full strength. */
export const CHARGE_FULL = 0.85;

/**
 * SPECIALIZED-ACTOR SCALE (F2 audit, 2026-08-26). The one place render
 * scale and sim contact radius both read from, for every enemy tier that
 * gets its own animated actor rig instead of the mass-instanced crowd.
 *
 * Before this, each side authored its own number and they were kept in
 * sync BY HAND: stormhulk's render scale (`TRUE_BOSS_SCALE`, formerly a
 * scene3d.ts-local constant) and its contact radius (`enemies.csv`'s
 * `radius` column) had to be hand-recomputed and re-checked every time
 * either moved. gale_colossus never got that treatment at all -- its
 * render scale was the flat generic `BOSS_SCALE`, unrelated to its
 * authored contact radius, which is why its silhouette could loom over
 * the squad while its actual hit-circle sat well inside it, and none of
 * the boss melee/scale/contact work that fixed stormhulk ever reached it
 * (it routes through the BEAST clamp in step.ts, a separate branch).
 *
 * `radius = scale * RADIUS_PER_SCALE` for every tier below. Ordinary
 * fodder (rift_brute included) is UNCHANGED and needs no entry here --
 * `scene3d.ts`'s crowd-actor scale is already `1.05 * (radius / 1.05)`,
 * which is `radius` itself: its render scale already equals its own
 * authored CSV radius by construction, so there is nothing to derive.
 *
 * RE-MEASURED (2026-08-26, boss overlap finding): 0.375 was fit to the
 * BODY, not to the drawn mesh at the point in its attack loop where the
 * swinging limb reaches furthest -- exactly the "limb overlaps the
 * squad" defect. Measured directly (real 560x940 capture, actor parked
 * at its own stop distance, sampled across a full attack-clip loop): at
 * TRUE_BOSS_SCALE=3.5 the mesh's nearest point reaches ~3.71 world units
 * from the pivot at the swing's worst frame, not the ~1.31 the old ratio
 * implied -- a ~2.8x understatement. 1.1 is that measured ratio with a
 * small margin, so the stop distance derived from it (radius +
 * playerHitRadius, step.ts) clears the actual swing, not just the body.
 */
export const RADIUS_PER_SCALE = 1.4;
/**
 * stormhulk -- the chapter's own true boss. Value and derivation history:
 * scene3d.ts's former `TRUE_BOSS_SCALE`, re-solved from real 560x940
 * captures at its actual melee stop distance.
 */
export const TRUE_BOSS_SCALE = 3.5;
/** Generic elite/boss actor pool scale, for anything `eIsBoss` flags that
 *  is neither the chapter's true boss nor an authored beast. */
export const BOSS_SCALE = 6;
/**
 * gale_colossus -- isle 1's beast. RE-MEASURED (2026-08-26, boss overlap
 * finding): 5.0 read as ~66.5% of frame height at its own stop distance
 * (real 560x940 capture, head clipped off the top of frame) -- more than
 * double the 1/3 ceiling, despite scale/distance sitting close to
 * stormhulk's own ratio; this rig's silhouette does not scale the same
 * way stormhulk's did at this pose. Re-picked directly from that same
 * capture rather than re-derived from the ratio.
 */
export const BEAST_SCALE = 2.0;

/**
 * BOSS_HP_MULT ANCHOR (2026-08-27, founder directive after 6 re-fits in
 * 5 days: 8.0 -> 0.0836 -> 0.02926 -> 1.09221 -> 2.35 -> 0.5875, none of
 * which survived the NEXT unrelated fix to depth, weapon0_damage_override
 * or the contact-damage model). Every prior derivation solved boss_hp_mult
 * against something that MOVES for reasons that have nothing to do with
 * boss HP: a simulated crowd-at-boss-spawn size, a specific reference
 * policy's play quality, or a per-chapter weapon override tuned for
 * fodder visibility. This anchor uses only quantities that do not move
 * run to run and are not touched by any of that:
 *
 *   1. rules.csv `squad_max` (Rules.squadMax) -- a fixed global, 20.
 *   2. weapons.csv row `chapters.csv[chapter].weapon_cap` selects, columns
 *      `damage_per_hit` and `cooldown_sec` ONLY -- read RAW from
 *      weapons.csv, never through `weapon0_damage_override` (that column
 *      only ever touches weapon index 0; both isles' own ceiling tier is
 *      a different index, so this is moot today, but the rule is
 *      absolute: this formula never reads a per-chapter override).
 *
 *   DPS_ceiling = squadMax * damage_per_hit / cooldown_sec
 *   boss_hp_mult = (DPS_ceiling * BOSS_TTK_TARGET_SEC) / 720000 (stormhulk's base hp)
 *
 * No projectiles_per_shot, no pierce, no volley cap, no crowd size, no
 * simulation of any kind -- a pure read of two CSVs. Re-deriving this
 * number after ANY future change requires only re-reading whichever of
 * those two files moved; it is invalidated by nothing else, including a
 * change to depth, enemy_hp_mult, weapon0_damage_override, waves, or the
 * contact-damage model.
 *
 * BOSS_TTK_TARGET_SEC = 20: authored, not measured. Long enough to read
 * as a real fight rather than the "one volley" and "coin flip" failure
 * modes every prior derivation's own commit message names; short enough
 * to stay proportionate against a 45-60s isle spending its finale on one
 * encounter. A round number chosen so the next person re-deriving this
 * can hold it in their head without consulting this file.
 */
export const BOSS_TTK_TARGET_SEC = 20;
